import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { DirhamIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useIsAdhaJourney, useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useAppStore } from '@/core/stores/appStore';
import { ProfileContactCard } from '@/modules/profile/components/ProfileContactCard';

function formatLoanAmount(amount: number | undefined): string {
    if (amount == null || !Number.isFinite(amount)) return "—";
    if (amount >= 1_000_000) {
        const millions = amount / 1_000_000;
        // AC-04: round to at most 2 decimals, dropping trailing zeros —
        // 1 -> "1", 1.5 -> "1.5", 1.75 -> "1.75" (not "1.50").
        const rounded = Math.round(millions * 100) / 100;
        return `${Number(rounded.toFixed(2)).toString()} M`;
    }
    return amount.toLocaleString();
}

export function LoanDetailsScreen() {
    const { t } = useTranslation();
    const isAdhaJourney = useIsAdhaJourney();
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const loanDetails = useAppStore((s) => s.adhaLoanDetails);

    useCatalogDynatraceView(DT_SCREEN.LOAN_DETAILS, 'loan_details_viewed', true);
    useCatalogDynatraceView(
        ALMETLAA_SCREEN.PROFILE,
        ALMETLAA_ACTION.PROFILE.LOAN_DETAILS_VIEW,
        isAdhaJourney,
    );

    const applicationNumber = loanDetails?.adhaApplicationNumber || "—";
    const sanctionedDisplay = useMemo(
        () => formatLoanAmount(loanDetails?.sanctionedLoanAmount),
        [loanDetails?.sanctionedLoanAmount],
    );

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader hideLogo={true} showLanguageSwitch={false} />

            <View style={styles.content}>
                <Typography variant="title1" color="primary" style={{ marginBottom: 16 }}>
                    {t('profile.loanDetails' as any)}
                </Typography>

                <ProfileContactCard
                    title={t('profile.applicationNumber' as any)}
                    value={
                        <Typography variant="body" color="secondary">
                            {applicationNumber}
                        </Typography>
                    }
                />

                <ProfileContactCard
                    title={t('profile.sanctionedLoanAmount' as any)}
                    value={
                        <View style={[styles.amountRow, { direction: 'ltr', justifyContent: isRTL ? 'flex-end' : 'flex-start' }]}>
                            <DirhamIcon size={16} color={colors.fontSecondary} />
                            <Typography variant="body" color="secondary">
                                {sanctionedDisplay}
                            </Typography>
                        </View>
                    }
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    content: {
        paddingTop: 32,
        paddingHorizontal: 16,
        gap: 12,
    },
    amountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
});
